import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import { ChevronDown, ChevronRight, ThreeDotsVertical } from "react-bootstrap-icons";
import { AuthService, CustomerService, EmployeeService, EventsService } from "../../services";
import { EMPLOYEE_PERMISSION_GROUPS, getHrTitleDisplayName, visibleEmployeePermissions } from "../../shared";

const SYSTEM_ACCESS_PERMISSION = "System Access";

const formatPermissionCount = (count) => {
  const n = Number(count) || 0;
  return `${n} Permission${n === 1 ? "" : "s"}`;
};

const EmployeeList = () => {
  const navigate = useNavigate();
  const currentSite = EventsService.site || 3;
  const [employees, setEmployees] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [isHrLoading, setIsHrLoading] = useState(false);
  const [isSavingHrPermission, setIsSavingHrPermission] = useState(false);
  const [hrKeyword, setHrKeyword] = useState("");
  const [hrPermissionMap, setHrPermissionMap] = useState({});
  const [hrPermissionRecords, setHrPermissionRecords] = useState([]);
  const [editingHrUser, setEditingHrUser] = useState(undefined);
  const [showHrPermissionModal, setShowHrPermissionModal] = useState(false);
  const [selectedHrPermissions, setSelectedHrPermissions] = useState([SYSTEM_ACCESS_PERMISSION]);
  const [hrPreferredName, setHrPreferredName] = useState("");
  const [expandedHrRows, setExpandedHrRows] = useState(() => new Set());
  const [expandedEmployeeRows, setExpandedEmployeeRows] = useState(() => new Set());

  const routeReportManagerSigStorageKey = `route_report_manager_signature_site_${currentSite}`;
  const [routeReportManagerSig, setRouteReportManagerSig] = useState(undefined);
  const [routeReportManagerSigFile, setRouteReportManagerSigFile] = useState(undefined);
  const [routeReportManagerSigInputKey, setRouteReportManagerSigInputKey] = useState(0);
  const [routeReportManagerSigBusy, setRouteReportManagerSigBusy] = useState(false);

  useEffect(() => {
    if (!AuthService.canViewEmployees()) {
      window.alert(
        "You haven't login yet OR this user does not have access to this page. Please change an admin account to login."
      );
      AuthService.logout();
      navigate(`/login`);
    }
    EmployeeService.getAllEmployees().then((data) => setEmployees(data.data));
    loadHrPermissionsBySite(currentSite);
  }, []);

  useEffect(() => {
    CustomerService.getAvatar(routeReportManagerSigStorageKey)
      .then((res) => setRouteReportManagerSig(res?.data || undefined))
      .catch(() => setRouteReportManagerSig(undefined));
  }, [routeReportManagerSigStorageKey]);

  const loadHrPermissionsBySite = (site) => {
    setIsHrLoading(true);
    EmployeeService.getExternalUserPermissionsList(site)
      .then((response) => {
        const records = Array.isArray(response?.data) ? response.data : [];
        const nextMap = {};
        records.forEach((item) => {
          const key = item?.external_user_id;
          if (!key) return;
          nextMap[key] = Array.isArray(item?.permissions) ? item.permissions : [];
        });
        setHrPermissionMap(nextMap);
        setHrPermissionRecords(records);
      })
      .catch(() => {
        setHrPermissionMap({});
        setHrPermissionRecords([]);
      })
      .finally(() => {
        setIsHrLoading(false);
      });
  };

  const goToEdit = (id) => {
    navigate(`/employees/edit/${id}`);
  };

  const goToView = (id) => {
    navigate(`/employees/${id}`);
  };

  const goToCreate = () => {
    navigate(`/employees`);
  };

  const goToExternalImport = () => {
    navigate(`/employees/external-import`);
  };

  const getExternalUserId = (item) => item?.external_user_id || item?.employee_id || "";

  const filteredHrUsers = useMemo(() => {
    return (hrPermissionRecords || [])
      .filter((item) => Number(item?.allow_site) === Number(currentSite))
      .filter((item) => {
        if (!hrKeyword) return true;
        const key = hrKeyword.toLowerCase();
        return (
          (item?.username || "").toLowerCase().includes(key) ||
          (item?.name || "").toLowerCase().includes(key) ||
          (item?.preferred_name || "").toLowerCase().includes(key)
        );
      });
  }, [hrPermissionRecords, hrKeyword, currentSite]);

  const filteredEmployees = useMemo(() => {
    const key = keyword.toLowerCase();
    return (employees || [])
      .filter((item) => (showInactive ? true : item.status === "active"))
      .filter((item) => (item?.name || "").toLowerCase().includes(key));
  }, [employees, keyword, showInactive]);

  const toggleHrRow = (rowKey) => {
    setExpandedHrRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const toggleEmployeeRow = (rowKey) => {
    setExpandedEmployeeRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const openHrPermissionModal = (hrUser) => {
    setEditingHrUser(hrUser);
    const externalUserId = getExternalUserId(hrUser);
    const existingPermissions = hrPermissionMap?.[externalUserId] || [];
    setSelectedHrPermissions(
      Array.from(new Set([SYSTEM_ACCESS_PERMISSION, ...existingPermissions]))
    );
    setHrPreferredName(`${hrUser?.preferred_name || ""}`);
    setShowHrPermissionModal(true);
  };

  const closeHrPermissionModal = () => {
    if (isSavingHrPermission) return;
    setShowHrPermissionModal(false);
    setEditingHrUser(undefined);
    setSelectedHrPermissions([SYSTEM_ACCESS_PERMISSION]);
    setHrPreferredName("");
  };

  const toggleHrPermission = (permissionKey) => {
    if (permissionKey === SYSTEM_ACCESS_PERMISSION) return;
    setSelectedHrPermissions((prev) => {
      if (prev.includes(permissionKey)) {
        return prev.filter((item) => item !== permissionKey);
      }
      return [...prev, permissionKey];
    });
  };

  const isHrPermissionGroupFullySelected = (permissionItems = []) => {
    return (
      permissionItems.length > 0 &&
      permissionItems.every((permissionKey) => selectedHrPermissions.includes(permissionKey))
    );
  };

  const toggleHrPermissionGroup = (permissionItems = [], checked) => {
    if (!Array.isArray(permissionItems) || permissionItems.length === 0) return;
    setSelectedHrPermissions((prev) => {
      const nextSet = new Set(prev);
      permissionItems.forEach((permissionKey) => {
        if (permissionKey === SYSTEM_ACCESS_PERMISSION) {
          nextSet.add(SYSTEM_ACCESS_PERMISSION);
          return;
        }
        if (checked) {
          nextSet.add(permissionKey);
        } else {
          nextSet.delete(permissionKey);
        }
      });
      return Array.from(nextSet);
    });
  };

  const saveHrPermissions = () => {
    const externalUserId = getExternalUserId(editingHrUser);
    if (!externalUserId) return;
    setIsSavingHrPermission(true);
    EmployeeService.saveExternalUserPermission({
      external_user_id: externalUserId,
      username: editingHrUser.username || "",
      name: editingHrUser.name || "",
      preferred_name: `${hrPreferredName || ""}`.trim(),
      title: editingHrUser.title || "",
      email: editingHrUser.email || "",
      allow_site: Number(currentSite),
      permissions: selectedHrPermissions,
    })
      .then(() => {
        setHrPermissionMap((prev) => ({
          ...prev,
          [externalUserId]: Array.from(
            new Set([SYSTEM_ACCESS_PERMISSION, ...selectedHrPermissions])
          ),
        }));
        loadHrPermissionsBySite(currentSite);
        closeHrPermissionModal();
      })
      .catch((error) => {
        window.alert(error?.response?.data?.message || "Failed to save HR user permissions.");
      })
      .finally(() => {
        setIsSavingHrPermission(false);
      });
  };

  const uploadRouteReportManagerSignature = async () => {
    if (!routeReportManagerSigFile) {
      window.alert("Choose an image file first.");
      return;
    }
    const fd = new FormData();
    fd.append("file", routeReportManagerSigFile);
    setRouteReportManagerSigBusy(true);
    try {
      if (routeReportManagerSig) {
        await CustomerService.deleteFile({ name: routeReportManagerSigStorageKey });
      }
      await CustomerService.uploadAvatar(routeReportManagerSigStorageKey, fd);
      const res = await CustomerService.getAvatar(routeReportManagerSigStorageKey);
      setRouteReportManagerSig(res?.data || undefined);
      setRouteReportManagerSigFile(undefined);
      setRouteReportManagerSigInputKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      window.alert("Could not upload manager signature. Please try again.");
    } finally {
      setRouteReportManagerSigBusy(false);
    }
  };

  const deleteRouteReportManagerSignature = async () => {
    if (!routeReportManagerSig) return;
    if (!window.confirm(`Remove the route report manager signature for site ${currentSite}?`)) return;
    setRouteReportManagerSigBusy(true);
    try {
      await CustomerService.deleteFile({ name: routeReportManagerSigStorageKey });
      setRouteReportManagerSig(undefined);
      setRouteReportManagerSigFile(undefined);
      setRouteReportManagerSigInputKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      window.alert("Could not remove manager signature. Please try again.");
    } finally {
      setRouteReportManagerSigBusy(false);
    }
  };

  const revokeHrPermissions = (hrUser) => {
    const externalUserId = getExternalUserId(hrUser);
    if (!externalUserId) return;
    if (
      !window.confirm(
        `Revoke all permissions for ${hrUser?.username || "this HR user"} on Site ${currentSite}?`
      )
    ) {
      return;
    }
    EmployeeService.revokeExternalUserPermission(externalUserId, Number(currentSite))
      .then(() => {
        setHrPermissionMap((prev) => {
          const next = { ...prev };
          delete next[externalUserId];
          return next;
        });
        loadHrPermissionsBySite(currentSite);
      })
      .catch((error) => {
        window.alert(error?.response?.data?.message || "Failed to revoke HR user permissions.");
      });
  };

  const renderPermissionDetail = (permissions) => {
    const list = visibleEmployeePermissions(Array.isArray(permissions) ? permissions.filter(Boolean) : []);
    return (
      <div className="employee-perm-detail">
        <strong className="employee-perm-detail__label">Configured Permissions:</strong>{" "}
        <span className="employee-perm-detail__list">
          {list.length > 0 ? list.join(", ") : "None"}
        </span>
      </div>
    );
  };

  return (
    <>
      <div className="list row mb-4">
        <div className="col-md-12 text-primary">
          <h5>
            All Employees
            {AuthService.canAddOrEditEmployees() && (
              <button className="btn btn-primary btn-sm ms-2" onClick={() => goToCreate()}>
                Add New Employee
              </button>
            )}
            {AuthService.canAddOrEditEmployees() && (
              <button className="btn btn-primary btn-sm ms-2" onClick={() => goToExternalImport()}>
                Add New Employee From HR System
              </button>
            )}
          </h5>
        </div>
      </div>
      <div className="list row mb-4">
        <div className="col-md-12">
          <h6 className="text-primary">HR System Users</h6>
          <div className="mb-3 d-flex align-items-center" style={{ gap: "12px", flexWrap: "wrap" }}>
            <label>
              Site: <strong className="ms-2">{currentSite}</strong>
            </label>
            <label>
              Filter:
              <input
                className="ms-2"
                type="text"
                value={hrKeyword}
                onChange={(e) => setHrKeyword(e.currentTarget.value)}
              />
            </label>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => loadHrPermissionsBySite(currentSite)}
              disabled={isHrLoading}
            >
              {isHrLoading ? "Loading..." : "Refresh HR Users"}
            </button>
          </div>
          <table className="personnel-info-table employee-perm-table mb-5">
            <thead>
              <tr>
                <th className="employee-perm-table__toggle-col" aria-label="Expand" />
                <th>Name</th>
                <th>Preferred Name</th>
                <th>Role / Title</th>
                <th>Site</th>
                <th>Permissions</th>
                <th className="employee-perm-table__action-col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isHrLoading && (
                <tr>
                  <td colSpan={7}>
                    <Spinner size="sm" className="me-2" />
                    Loading HR users...
                  </td>
                </tr>
              )}
              {!isHrLoading &&
                filteredHrUsers.map((hrUser) => {
                  const externalUserId = getExternalUserId(hrUser);
                  const rowKey = `${externalUserId}-${hrUser?.allow_site}`;
                  const configuredPermissions = visibleEmployeePermissions(
                    hrPermissionMap?.[externalUserId] || hrUser?.permissions || []
                  );
                  const isExpanded = expandedHrRows.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr className={isExpanded ? "employee-perm-row employee-perm-row--expanded" : "employee-perm-row"}>
                        <td className="employee-perm-table__toggle-col">
                          <button
                            type="button"
                            className="employee-perm-toggle"
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? "Collapse permissions" : "Expand permissions"}
                            onClick={() => toggleHrRow(rowKey)}
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td>
                          <div className="employee-perm-name">{hrUser?.name || "—"}</div>
                          {hrUser?.username ? (
                            <div className="employee-perm-subtitle">{hrUser.username}</div>
                          ) : null}
                        </td>
                        <td>{hrUser?.preferred_name || "—"}</td>
                        <td>{getHrTitleDisplayName(hrUser?.title || "-")}</td>
                        <td>{hrUser?.allow_site || currentSite}</td>
                        <td>{formatPermissionCount(configuredPermissions.length)}</td>
                        <td className="employee-perm-table__action-col">
                          <Dropdown align="end">
                            <Dropdown.Toggle
                              variant="link"
                              className="employee-perm-actions-toggle"
                              id={`hr-actions-${rowKey}`}
                            >
                              <ThreeDotsVertical size={16} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openHrPermissionModal(hrUser)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => revokeHrPermissions(hrUser)}>
                                Revoke
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="employee-perm-detail-row">
                          <td colSpan={7}>{renderPermissionDetail(configuredPermissions)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              {!isHrLoading && filteredHrUsers.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No HR users found for selected site.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <h6 className="text-primary">Internal Employees</h6>
          <div className="mb-4">
            Filter By Name:{" "}
            <input type="text" value={keyword} onChange={(e) => setKeyword(e.currentTarget.value)} />
          </div>
          <input
            className="mb-4 me-2"
            type="checkbox"
            checked={showInactive === true}
            onChange={() => setShowInactive(!showInactive)}
          />
          Show Inactive Employees
          <table className="personnel-info-table employee-perm-table">
            <thead>
              <tr>
                <th className="employee-perm-table__toggle-col" aria-label="Expand" />
                <th>Name</th>
                <th>Preferred Name</th>
                <th>Username</th>
                <th>Status</th>
                <th>Permissions</th>
                <th className="employee-perm-table__action-col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => {
                const rowKey = String(employee.id);
                const permissions = visibleEmployeePermissions(
                  Array.isArray(employee?.permissions) ? employee.permissions : []
                );
                const isExpanded = expandedEmployeeRows.has(rowKey);
                return (
                  <React.Fragment key={rowKey}>
                    <tr className={isExpanded ? "employee-perm-row employee-perm-row--expanded" : "employee-perm-row"}>
                      <td className="employee-perm-table__toggle-col">
                        <button
                          type="button"
                          className="employee-perm-toggle"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? "Collapse permissions" : "Expand permissions"}
                          onClick={() => toggleEmployeeRow(rowKey)}
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td>{employee?.name}</td>
                      <td>{employee?.name_cn}</td>
                      <td>{employee?.username}</td>
                      <td>{employee?.status}</td>
                      <td>{formatPermissionCount(permissions.length)}</td>
                      <td className="employee-perm-table__action-col">
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="link"
                            className="employee-perm-actions-toggle"
                            id={`emp-actions-${rowKey}`}
                          >
                            <ThreeDotsVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {AuthService.canAddOrEditEmployees() && (
                              <Dropdown.Item onClick={() => goToEdit(employee?.id)}>Edit</Dropdown.Item>
                            )}
                            {AuthService.canViewEmployees() && (
                              <Dropdown.Item onClick={() => goToView(employee?.id)}>View</Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="employee-perm-detail-row">
                        <td colSpan={7}>{renderPermissionDetail(permissions)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredEmployees.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No employees found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <h6 className="text-primary mt-5">Route report — manager signature</h6>
          <p className="text-muted small mb-3">
            Image used as <strong>Manager&apos;s signature</strong> on the route report with signature page for{" "}
            <strong>site {currentSite}</strong>. It is stored on the server (same folder as other center signatures).
          </p>
          <div className="d-flex flex-wrap align-items-start gap-3 mb-3">
            {routeReportManagerSig && (
              <div>
                <div className="text-muted small mb-1">Current</div>
                <img
                  height="100"
                  width="100"
                  alt="Route report manager signature"
                  src={`data:image/jpg;base64, ${routeReportManagerSig}`}
                />
              </div>
            )}
            {!routeReportManagerSig && (
              <div className="text-muted small">
                No signature uploaded yet — the report will use the default image until one is set.
              </div>
            )}
          </div>
          {AuthService.canAddOrEditEmployees() && (
            <div className="mb-5 d-flex flex-wrap align-items-end gap-2">
              <label className="mb-0">
                Upload image
                <input
                  key={routeReportManagerSigInputKey}
                  type="file"
                  accept="image/*"
                  className="d-block mt-1"
                  disabled={routeReportManagerSigBusy}
                  onChange={(e) => setRouteReportManagerSigFile(e.target.files?.[0] || undefined)}
                />
              </label>
              <Button
                variant="primary"
                size="sm"
                disabled={routeReportManagerSigBusy || !routeReportManagerSigFile}
                onClick={uploadRouteReportManagerSignature}
              >
                {routeReportManagerSigBusy ? "Saving…" : "Save signature"}
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                disabled={routeReportManagerSigBusy || !routeReportManagerSig}
                onClick={deleteRouteReportManagerSignature}
              >
                Delete signature
              </Button>
            </div>
          )}
        </div>
      </div>
      <Modal show={showHrPermissionModal} onHide={closeHrPermissionModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit HR User Permissions - {editingHrUser?.username}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Allow Site:</strong> {currentSite}
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="hr-preferred-name">
              Preferred Name
            </label>
            <input
              id="hr-preferred-name"
              type="text"
              className="form-control"
              value={hrPreferredName}
              onChange={(e) => setHrPreferredName(e.currentTarget.value)}
              disabled={isSavingHrPermission}
            />
          </div>
          {Object.entries(EMPLOYEE_PERMISSION_GROUPS).map(([groupName, groupPermissions]) => {
            const permissionItems = visibleEmployeePermissions(groupPermissions);
            return (
            <div key={groupName} className="mb-3">
              <div
                className="d-flex align-items-center justify-content-between mb-1"
                style={{ gap: "16px" }}
              >
                <div className="text-primary">{groupName}</div>
                <label>
                  <input
                    type="checkbox"
                    className="me-2"
                    checked={isHrPermissionGroupFullySelected(permissionItems)}
                    onChange={(e) => toggleHrPermissionGroup(permissionItems, e.currentTarget.checked)}
                    disabled={groupName === "System"}
                  />
                  Select All
                </label>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 18px" }}>
                {permissionItems.map((permissionKey) => (
                  <label key={permissionKey} style={{ minWidth: "280px" }}>
                    <input
                      type="checkbox"
                      className="me-2"
                      checked={selectedHrPermissions.includes(permissionKey)}
                      onChange={() => toggleHrPermission(permissionKey)}
                      disabled={permissionKey === SYSTEM_ACCESS_PERMISSION}
                    />
                    {permissionKey}
                    {permissionKey === SYSTEM_ACCESS_PERMISSION ? " (required)" : ""}
                  </label>
                ))}
              </div>
            </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeHrPermissionModal} disabled={isSavingHrPermission}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveHrPermissions} disabled={isSavingHrPermission}>
            {isSavingHrPermission ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Done"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EmployeeList;
